"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type HighlightTone,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type AcademicCount,
  type AcademicCountOptions,
  academicWordCount,
  EXCLUSION_KINDS,
  type ExclusionKind,
} from "@/lib/tool-kit/text-analysis/academic-count";
import { collapseWhitespace } from "@/lib/tool-kit/text-analysis/segment";

/** English strings. A translated content file overrides these through `ui`. */
export const ACADEMIC_WORD_COUNTER_STRINGS = {
  textLabel: "Your essay or paper",
  textPlaceholder:
    "Paste the whole paper — title, headings, citations, and reference list included. The counter works out which words count.",
  trySample: "Try a sample",
  clear: "Clear",
  rulesLabel: "Leave out",
  rules: {
    references:
      "The reference list — everything after a References, Works Cited, Bibliography, or Reference List heading",
    headings: "Headings and the title line",
    quotations:
      "Quotations — words in double quotation marks, and paragraphs that start with >",
    citations:
      "Parenthetical citations — (Smith et al., 2020, p. 4), (Lee 45), (2021)",
    numeric: "Numeric citations and superscript markers — [1], [2–4], ¹²",
  } as Record<ExclusionKind, string>,
  numericHint:
    "Footnote numbers that lost their superscript formatting in the paste (study.12) are left in: they cannot be told apart from ordinary numbers.",
  limitLabel: "Word limit (optional)",
  limitPlaceholder: "2000",
  emptyPrompt: "Paste your text to see which words count.",
  results: "Results",
  counted: "Words that count",
  total: "Words in the text",
  excluded: "Words left out",
  limitUnder:
    "one:{count} word under your limit of {limit}.|other:{count} words under your limit of {limit}.",
  limitOver:
    "one:{count} word over your limit of {limit}.|other:{count} words over your limit of {limit}.",
  limitExact: "Exactly at your limit of {limit}.",
  referenceFound: "Reference list found at the “{heading}” heading.",
  referenceMissing:
    "No reference list heading found, so the whole text is treated as the body.",
  breakdownHeading: "What each rule found",
  colRule: "Rule",
  colFound: "Found",
  colWords: "Words left out",
  ruleOff: "counted",
  kindNames: {
    references: "Reference list",
    headings: "Headings",
    quotations: "Quotations",
    citations: "Parenthetical citations",
    numeric: "Numeric citations and markers",
  } as Record<ExclusionKind, string>,
  highlightHeading: "Your text, left-out words highlighted",
  highlightLabel: "Your text with the words left out of the count highlighted",
  listHeading: "{kind} ({count})",
  institutionNote:
    "Institutions differ: almost all leave out the reference list, but many count in-text citations, quotations, and headings. Follow your course or department's word-count policy.",
  copySummary: "Copy counts",
  copied: "Copied!",
  copyText:
    "Words that count: {counted}. Words in the text: {total}. Left out: {excluded}.",
  sample:
    "Sleep and Exam Performance\n\nIntroduction\nMost students treat sleep as the first thing to cut before an exam. The evidence points the other way: sleep after learning helps memory consolidation (Okafor, 2021), and short sleep before a test is linked to lower scores (Lindqvist & Mehta, 2019, p. 7; Brandt et al., 2022).\n\nDiscussion\nOne review calls the night before an exam “the last study session you can still schedule” (Okafor, 2021, p. 30). Students who protect it tend to do better.\n\nReferences\nBrandt, L., Osei, K., & Ruiz, M. (2022). Sleep timing and test scores. Learning and Health, 4(1), 15–29.\nLindqvist, E., & Mehta, R. (2019). Sleep before exams. Journal of Student Wellbeing, 12(2), 1–12.\nOkafor, C. (2021). The resting brain. Northfield University Press.",
} as const;

type Strings = typeof ACADEMIC_WORD_COUNTER_STRINGS;

const TONES: Record<ExclusionKind, HighlightTone> = {
  references: "sky",
  headings: "violet",
  quotations: "amber",
  citations: "emerald",
  numeric: "rose",
};

const DEFAULT_RULES: AcademicCountOptions = {
  references: true,
  headings: false,
  quotations: false,
  citations: false,
  numeric: false,
};

const LIST_LIMIT = 100;
const EXCERPT_LENGTH = 90;

function excerpt(text: string): string {
  const flat = collapseWhitespace(text);
  return flat.length > EXCERPT_LENGTH
    ? `${flat.slice(0, EXCERPT_LENGTH)}…`
    : flat;
}

function LimitLine({
  counted,
  limit,
  strings,
}: {
  counted: number;
  limit: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const gap = Math.abs(limit - counted);
  const values = { count: fmt(gap), limit: fmt(limit) };
  let message = t(strings.limitExact, values);
  if (counted < limit) {
    message = tPlural(strings.limitUnder, locale, gap, values);
  } else if (counted > limit) {
    message = tPlural(strings.limitOver, locale, gap, values);
  }
  return <p className="font-medium text-foreground text-sm">{message}</p>;
}

function Breakdown({
  count,
  rules,
  strings,
}: {
  count: AcademicCount;
  rules: AcademicCountOptions;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.breakdownHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colRule}
              </th>
              <th className={`${TH} whitespace-nowrap`} scope="col">
                {strings.colFound}
              </th>
              <th className={`${TH} whitespace-nowrap`} scope="col">
                {strings.colWords}
              </th>
            </tr>
          </thead>
          <tbody>
            {EXCLUSION_KINDS.map((kind) => (
              <tr key={kind}>
                <td className={TD}>
                  <span className={HIGHLIGHT_TONES[TONES[kind]]}>
                    {strings.kindNames[kind]}
                  </span>
                </td>
                <td className={TD}>{fmt(count.kinds[kind].items)}</td>
                <td className={TD}>
                  {rules[kind] ? fmt(count.kinds[kind].words) : strings.ruleOff}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExclusionLists({
  text,
  count,
  rules,
  strings,
}: {
  text: string;
  count: AcademicCount;
  rules: AcademicCountOptions;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const kinds = EXCLUSION_KINDS.filter(
    (kind) =>
      kind !== "references" && rules[kind] && count.kinds[kind].items > 0
  );
  return (
    <div className="space-y-2">
      {kinds.map((kind) => {
        const items = count.found
          .filter((span) => span.kind === kind)
          .slice(0, LIST_LIMIT);
        return (
          <details
            className="rounded-lg border border-border bg-card p-3"
            key={kind}
          >
            <summary className={`${LABEL} cursor-pointer`}>
              {t(strings.listHeading, {
                kind: strings.kindNames[kind],
                count: fmt(count.kinds[kind].items),
              })}
            </summary>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground text-sm">
              {items.map((span) => (
                <li key={span.start}>
                  {excerpt(text.slice(span.start, span.end))}
                </li>
              ))}
            </ul>
          </details>
        );
      })}
    </div>
  );
}

function Results({
  text,
  count,
  rules,
  limit,
  strings,
}: {
  text: string;
  count: AcademicCount;
  rules: AcademicCountOptions;
  limit: number | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const values = {
    counted: fmt(count.counted),
    total: fmt(count.total),
    excluded: fmt(count.total - count.counted),
  };
  const matches = count.found
    .filter((span) => rules[span.kind])
    .map((span) => ({ start: span.start, end: span.end, category: span.kind }));
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.counted} value={values.counted} />
        <StatCard label={strings.total} value={values.total} />
        <StatCard label={strings.excluded} value={values.excluded} />
      </StatGrid>
      {limit === null ? null : (
        <LimitLine counted={count.counted} limit={limit} strings={strings} />
      )}
      <p className={HINT}>
        {count.referenceHeading
          ? t(strings.referenceFound, { heading: count.referenceHeading })
          : strings.referenceMissing}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySummary}
        text={t(strings.copyText, values)}
      />
      <Breakdown count={count} rules={rules} strings={strings} />
      <Notice>{strings.institutionNote}</Notice>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.highlightHeading}</h3>
        <HighlightedText
          label={strings.highlightLabel}
          matches={matches}
          text={text}
          tones={TONES}
        />
      </div>
      <ExclusionLists
        count={count}
        rules={rules}
        strings={strings}
        text={text}
      />
    </section>
  );
}

export default function AcademicWordCounterWidget() {
  const strings = useToolUi(ACADEMIC_WORD_COUNTER_STRINGS);
  const [text, setText] = useState("");
  const [rules, setRules] = useState<AcademicCountOptions>(DEFAULT_RULES);
  const [limitRaw, setLimitRaw] = useState("");
  const deferred = useDeferredValue(text);
  const count = useMemo(
    () => (deferred.trim() ? academicWordCount(deferred, rules) : null),
    [deferred, rules]
  );
  const parsedLimit = parseIntegerInput(limitRaw);
  const limit = parsedLimit !== null && parsedLimit > 0 ? parsedLimit : null;

  return (
    <>
      <TextAreaField
        id="awc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={12}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="space-y-2">
        <p className={LABEL}>{strings.rulesLabel}</p>
        {EXCLUSION_KINDS.map((kind) => (
          <Checkbox
            checked={rules[kind]}
            key={kind}
            label={strings.rules[kind]}
            onChange={(checked) =>
              setRules((current) => ({ ...current, [kind]: checked }))
            }
          />
        ))}
        <p className={HINT}>{strings.numericHint}</p>
      </div>
      <div className="max-w-xs">
        <TextField
          id="awc-limit"
          label={strings.limitLabel}
          numeric
          onChange={setLimitRaw}
          placeholder={strings.limitPlaceholder}
          value={limitRaw}
        />
      </div>
      {count ? (
        <Results
          count={count}
          limit={limit}
          rules={rules}
          strings={strings}
          text={deferred}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
